import { z } from "zod";
import { breakdownSchema } from "./schemas.ts";

export const defaultDashboardKindSchema = z.literal("builtin_route");

export const defaultDashboardStrategySchema = z.enum([
	"marketing_overview",
	"channel_dashboard",
	"creative_comparison",
	"creative_analysis",
	"realtime",
	"customer_journeys",
]);

export const defaultDashboardSupportLevelSchema = z.enum(["full", "partial"]);

export const defaultDashboardViewStateSchema = z
	.object({
		creativeChannel: z.enum(["meta", "tiktok"]).optional(),
		dashboardMode: z.enum(["ecommerce", "general"]).optional(),
	})
	.strict();

export const defaultDashboardDescriptorSchema = z
	.object({
		id: z.string().min(1),
		label: z.string().min(1),
		kind: defaultDashboardKindSchema,
		strategy: defaultDashboardStrategySchema,
		supportLevel: defaultDashboardSupportLevelSchema,
		primaryChannel: z.string().min(1).optional(),
		primaryBreakdown: breakdownSchema.optional(),
		viewState: defaultDashboardViewStateSchema.optional(),
	})
	.strict();

export type DefaultDashboardKind = z.infer<typeof defaultDashboardKindSchema>;
export type DefaultDashboardStrategy = z.infer<
	typeof defaultDashboardStrategySchema
>;
export type DefaultDashboardSupportLevel = z.infer<
	typeof defaultDashboardSupportLevelSchema
>;
export type DefaultDashboardViewState = z.infer<
	typeof defaultDashboardViewStateSchema
>;
export type DefaultDashboardDescriptor = z.infer<
	typeof defaultDashboardDescriptorSchema
>;

export type ResolveBuiltinDashboardDescriptorOptions = {
	creativeChannel?: "facebook" | "meta" | "tiktok" | null;
	dashboardMode?: "ecommerce" | "general" | null;
};

type BuiltinDashboardBase = Omit<
	DefaultDashboardDescriptor,
	"id" | "kind" | "viewState"
>;

const CORE_BUILTIN_DASHBOARDS = {
	"/": {
		label: "Marketing Overview",
		strategy: "marketing_overview",
		supportLevel: "full",
	},
	"/realtime": {
		label: "Realtime Dashboard",
		strategy: "realtime",
		supportLevel: "partial",
	},
	"/creatives/comparison": {
		label: "Creative Dashboard: Comparison",
		strategy: "creative_comparison",
		supportLevel: "full",
		primaryBreakdown: "creative",
	},
	"/creatives/analysis": {
		label: "Creative Dashboard: Individual Analysis",
		strategy: "creative_analysis",
		supportLevel: "full",
		primaryBreakdown: "creative",
	},
	"/customer-journeys": {
		label: "Customer Journeys",
		strategy: "customer_journeys",
		supportLevel: "partial",
	},
} as const satisfies Record<string, BuiltinDashboardBase>;

const CHANNEL_BUILTIN_DASHBOARDS = {
	meta: {
		label: "Meta Ads Dashboard",
		primaryChannel: "meta",
		primaryBreakdown: "creative",
	},
	google: {
		label: "Google Ads Dashboard",
		primaryChannel: "google",
		primaryBreakdown: "advertising_channel_type",
	},
	tiktok: {
		label: "TikTok Ads Dashboard",
		primaryChannel: "tiktok",
		primaryBreakdown: "creative",
	},
	pinterest: {
		label: "Pinterest Ads Dashboard",
		primaryChannel: "pinterest",
		primaryBreakdown: "creative",
	},
	outbrain: {
		label: "Outbrain Ads Dashboard",
		primaryChannel: "outbrain",
		primaryBreakdown: "creative",
	},
	microsoft: {
		label: "Microsoft Ads Dashboard",
		primaryChannel: "msads",
		primaryBreakdown: "creative",
	},
	taboola: {
		label: "Taboola Ads Dashboard",
		primaryChannel: "taboola",
		primaryBreakdown: "creative",
	},
} as const satisfies Record<
	string,
	Pick<
		DefaultDashboardDescriptor,
		"label" | "primaryChannel" | "primaryBreakdown"
	>
>;

export const BUILTIN_DASHBOARD_CORE_ITEMS = Object.entries(
	CORE_BUILTIN_DASHBOARDS,
).map(([id, dashboard]) => ({
	id,
	label: dashboard.label,
}));

export const BUILTIN_DASHBOARD_STRATEGY_GUIDANCE = {
	marketing_overview:
		"Start with getAnalyticsSnapshot, then use getKpisOverview or getKpisPerChannel for deeper reads.",
	channel_dashboard:
		"Use getKpisPerChannel for the primary channel plus getChannelKpiBreakdown for the cataloged breakdown.",
	creative_comparison:
		'Use getChannelKpiBreakdown with breakdown="creative" and the active creative channel.',
	creative_analysis:
		'Use getChannelKpiBreakdown with breakdown="creative" and the active creative channel.',
	realtime:
		"Approximate with channel-level KPIs and supported campaign breakdowns only; skip unsupported influencer breakdowns and do not invent influencer-specific rows.",
	customer_journeys:
		"Use getCustomerJourneys for journey overview, initiator/holder/closer roles, and conversion-path tables.",
} as const satisfies Record<DefaultDashboardStrategy, string>;

function normalizeCreativeChannel(
	creativeChannel: ResolveBuiltinDashboardDescriptorOptions["creativeChannel"],
): DefaultDashboardViewState["creativeChannel"] | undefined {
	if (!creativeChannel) {
		return undefined;
	}

	return creativeChannel === "facebook" ? "meta" : creativeChannel;
}

function buildViewState(
	options: ResolveBuiltinDashboardDescriptorOptions,
	includeCreativeChannel: boolean,
): DefaultDashboardViewState | undefined {
	const viewState: DefaultDashboardViewState = {};
	const creativeChannel = normalizeCreativeChannel(options.creativeChannel);

	if (includeCreativeChannel && creativeChannel) {
		viewState.creativeChannel = creativeChannel;
	}

	if (options.dashboardMode) {
		viewState.dashboardMode = options.dashboardMode;
	}

	return Object.keys(viewState).length > 0 ? viewState : undefined;
}

function buildDescriptor(
	id: string,
	base: BuiltinDashboardBase,
	options: ResolveBuiltinDashboardDescriptorOptions,
	includeCreativeChannel: boolean = false,
): DefaultDashboardDescriptor {
	const descriptor: DefaultDashboardDescriptor = {
		id,
		label: base.label,
		kind: "builtin_route",
		strategy: base.strategy,
		supportLevel: base.supportLevel,
		...(base.primaryChannel ? { primaryChannel: base.primaryChannel } : {}),
		...(base.primaryBreakdown
			? { primaryBreakdown: base.primaryBreakdown }
			: {}),
	};

	if (includeCreativeChannel) {
		const creativeChannel = normalizeCreativeChannel(options.creativeChannel);
		if (creativeChannel) {
			descriptor.primaryChannel = creativeChannel;
		}
	}

	const viewState = buildViewState(options, includeCreativeChannel);
	if (viewState) {
		descriptor.viewState = viewState;
	}

	return descriptor;
}

export function getBuiltinDashboardDescriptor(
	pathname: string,
	options: ResolveBuiltinDashboardDescriptorOptions = {},
): DefaultDashboardDescriptor | null {
	const coreDashboard =
		CORE_BUILTIN_DASHBOARDS[
			pathname as keyof typeof CORE_BUILTIN_DASHBOARDS
		];
	if (coreDashboard) {
		return buildDescriptor(
			pathname,
			coreDashboard,
			options,
			pathname === "/creatives/comparison" || pathname === "/creatives/analysis",
		);
	}

	const channelMatch = /^\/channels\/([^/]+)\/dashboard$/.exec(pathname);
	if (!channelMatch) {
		return null;
	}

	const routeSegment = channelMatch[1] as keyof typeof CHANNEL_BUILTIN_DASHBOARDS;
	const channelDashboard = CHANNEL_BUILTIN_DASHBOARDS[routeSegment];
	if (!channelDashboard) {
		return null;
	}

	return buildDescriptor(
		pathname,
		{
			label: channelDashboard.label,
			strategy: "channel_dashboard",
			supportLevel: "full",
			primaryChannel: channelDashboard.primaryChannel,
			primaryBreakdown: channelDashboard.primaryBreakdown,
		},
		options,
	);
}
